/**
 * 画面の文字の ja / en 出し分け。→ doc/i18n/master.md
 *
 * **i18n ライブラリは入れない。** 対象は2言語・キー200件ほどで、複数形も日付書式も要らない。
 * `src/tarkovJson.ts` の JSONPath を自前で書いているのと同じ判断（依存を増やさない）。
 *
 * ゲームデータ側（タスク名・目標文・アイテム名）の言語は `src/tarkovData.ts` が持つ。
 * こちらは**アプリが書いた文字だけ**を扱う。
 *
 * `/about` の権利表記は段落構造のある文書なので、ここには載せず
 * `About.tsx` の中で JSX ごと切り替える。
 */
import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react'
import type { Lang } from './types'

export type { Lang }

const LS_LANG = 'tm.lang'

export const LANGS: { id: Lang; label: string }[] = [
  { id: 'ja', label: 'JA' },
  { id: 'en', label: 'EN' },
]

const isLang = (v: unknown): v is Lang => v === 'ja' || v === 'en'

/**
 * 初回はブラウザの言語から決める。以後は選んだものを憶える。
 * 知らない値が入っていたらブラウザ判定に落とす（壊れた値で固定しない）。
 */
export function detectLang(): Lang {
  try {
    const saved = localStorage.getItem(LS_LANG)
    if (isLang(saved)) return saved
  } catch {
    /* localStorage が使えない環境でも動かす */
  }
  return navigator.language?.toLowerCase().startsWith('ja') ? 'ja' : 'en'
}

/* ------------------------------------------------------------------ *
 * 辞書
 * ------------------------------------------------------------------ */

const ja = {
  /* ---- 言語 ---- */
  'lang.aria': '表示言語',

  /* ---- ツール ---- */
  'tool.hand.label': '🖐 操作',
  'tool.hand.title': 'ドラッグでマップを動かします。ピンの選択・移動もできます',
  'tool.pin.label': '📍 ピン',
  'tool.pin.title': 'クリックでピンを置きます',
  'tool.pen.label': '✏️ ペン',
  'tool.pen.title': 'ドラッグでマップに線を描きます（Space を押している間は移動）',
  'tool.eraser.label': '🧹 消しゴム',
  'tool.eraser.title': '落書きを消します（Space を押している間は移動）',

  /* ---- ツールバー ---- */
  'app.map': 'マップ',
  'app.floor': 'フロア',
  'app.floor.title': '地上に重ねて出す階を選びます',
  'app.floor.ground': '地上（既定）',
  'app.tools': 'ツール',
  'app.penColor': '線の色',
  'app.pinColor': 'ピンの色',
  'app.color': '色 {c}',
  'app.penWidth': '線の太さ',
  'app.penWidth.step': '線の太さ {size}',
  'app.size.thin': '細',
  'app.size.medium': '中',
  'app.size.thick': '太',
  'app.eraserSize': '消しゴムの大きさ',
  'app.eraserSize.step': '消しゴム {size}',
  'app.size.small': '小',
  'app.size.large': '大',
  'app.eraserMode': '消しゴムの粒度',
  'app.eraser.stroke': '線ごと',
  'app.eraser.stroke.title': 'なぞった線がまるごと消えます',
  'app.eraser.area': '部分',
  'app.eraser.area.title': 'なぞった範囲だけ消えます',
  'app.eraser.warn': '⚠ 切断しやすくなります。少しずつ実行、または「線ごと」をお使いください',
  'app.eraser.warn.title':
    'この操作は切断しやすくなります。少しずつ実行、もしくは「線ごと」の機能をお使いください',

  'app.on': 'ON',
  'app.off': 'OFF',
  'app.toggle.doodle': '🖊 落書き',
  'app.toggle.doodle.title': '落書きの表示を切り替えます',
  'app.toggle.list': '🗒 一覧',
  'app.toggle.list.title': '登録タスクの一覧を出し入れします',
  'app.toggle.locations': '📌 場所',
  'app.toggle.locations.title': '登録タスクの場所をマップに重ねます',
  'app.toggle.taskNames': '🔖 タスク名',
  'app.toggle.taskNames.title': 'マップの番号の上にタスク名を出します',
  'app.toggle.labels': '🏷 地名',
  'app.toggle.labels.title': '通り名・建物名を表示します（引くと主要なものだけになります）',
  'app.toggle.extracts': '🚪 脱出',
  'app.toggle.extracts.title': '脱出地点を表示します',

  'app.about': 'ℹ️ 情報',
  'app.about.title': 'このアプリの権利表記を表示します',
  'app.name': '名前',
  'app.state.connecting': '接続中…',
  'app.state.open': '接続済み',
  'app.state.closed': '切断 — 再接続します',
  'app.users': '{n}人',

  /* ---- タブ ---- */
  'app.tabs': '画面',
  'app.tab.map': '🗺 マップ',
  'app.tab.search': '🔍 検索・登録',

  /* ---- マップ画面 ---- */
  'app.tasksError': 'タスクデータを取得できません（マップ・ピン・落書きは使えます）: {msg}',
  'app.pin.label.placeholder': 'ラベル（例: スナイパー）',
  'app.pin.note.placeholder': 'メモ（任意）',
  'app.close': '閉じる',
  'app.delete': '削除',
  'app.menu.show': '▾ メニュー表示',
  'app.menu.hide': '▴ メニュー非表示',
  'app.menu.show.title': '共有バー・ツールバー・タブを出します',
  'app.menu.hide.title': 'マップと登録タスクだけにします（共有バー・ツールバー・タブを畳む）',
  'app.side.open': '⟨ 一覧',
  'app.side.open.title': '登録タスク一覧を開く',
  'app.footbar.counts': '{pins} ピン / {strokes} 本',
  'app.footbar.otherFloor': '別のフロアに {n} 件',
  'app.footbar.otherFloor.title': 'フロアを切り替えると出ます',
  'app.user.viewing': '{map} を見ています',
  'app.user.noMap': 'マップ未選択',
  'app.user.drawing': '描いています',
  'app.clearPins': 'ピン全消去',
  'app.clearPins.confirm': '「{map}」のピンを全部消します。よろしいですか？',
  'app.clearDoodle': '落書き全消去',
  'app.clearDoodle.confirm': '「{map}」の落書きを全部消します。よろしいですか？',

  /* ---- 検索・登録タブ ---- */
  'board.notice.error': 'タスクデータを取得できません（json.tarkov.dev）: {msg}',
  'board.notice.loading': 'タスクデータを読み込んでいます…',
  'board.people': '人数',
  'board.people.n': '{n}人',
  'board.hint': '{n}件のタスクから検索できます',

  /* ---- 共有バー ---- */
  'share.open.title': '共有バーを開く',
  'share.close.title': '共有バーを畳む',
  'share.label': '🔗 このURLを渡してください',
  'share.copy': 'コピー',
  'share.copied': '✓ コピーしました',
  'share.prompt': 'このURLをコピーしてください',
  'share.reconnect': '再接続中…',
  'share.leave': '退出',
  'share.leave.title': 'ルームから出てトップへ戻ります',
  'share.warn': 'このURLを知っている人は誰でも入れます。',
  'share.warn.strong': '秘密の情報を書かないでください。',
  'share.warn.note': 'ルームは最終更新から2日で消えます。',

  /* ---- 一覧パネル ---- */
  'side.title': '登録タスク',
  'side.width': '一覧パネルの幅',
  'side.close': '一覧を閉じる',

  /* ---- 登録タスク一覧 ---- */
  'tasklist.emptyForMap': 'このマップのタスクはありません（登録は {n} 件）。',
  'tasklist.emptyForMap.hint': 'マップを切り替えるか、「🔍 検索・登録」タブで登録してください',
  'tasklist.empty': '「🔍 検索・登録」タブでタスクを登録すると、ここに一覧が出ます',
  'tasklist.fold.open': '開く',
  'tasklist.fold.close': '畳む',
  'tasklist.fold.open.title': '目標や準備物を開きます',
  'tasklist.fold.close.title': 'タスク名と準備物マークだけにします',
  'tasklist.no.title': 'No.{n} — マップの数字と同じ番号です（ドラッグで順番を入れ替えられます）',
  'tasklist.wiki': 'wiki で開く',
  'tasklist.anyMap': '任意のマップ',
  'tasklist.nozone': '場所なし',
  'tasklist.nozone.title': 'このタスクには座標データがありません',
  'tasklist.slot': 'スロット{id}',
  'tasklist.optional': '任意',
  'tasklist.optional.title': 'やらなくてもタスクは完了します（マップの番号も灰色になります）',
  'task.kappa': 'Κ',
  'task.kappa.title': 'カッパコンテナ（Collector）に必要。Collector 本体とその前提チェーン13件です',
  'tasklist.needs': '前提: {names}',
  'tasklist.needs.title': '先に終わらせないと受けられないタスクです',
  'tasklist.sameObjective': '×{n}件',
  'tasklist.sameObjective.title': '別々の目標ですが、上流の訳文が同じなので1行にまとめています',
  'tasklist.remove': '全員から登録解除',
  'tasklist.show': 'マップに表示する',
  'tasklist.hide': 'マップから隠す',
  'tasklist.show.title': 'マップに図形を戻します（全員に反映されます）',
  'tasklist.hide.title': 'マップから図形を消します（全員に反映されます）',
  'tasklist.up': '上へ',
  'tasklist.down': '下へ',
  'tasklist.sort': 'A→Z',
  'tasklist.sort.title': 'このマップの一覧をタスク名の順に並べ替えます（番号ごと全員に反映されます）',

  /* ---- 1人ぶんの列（検索・登録） ---- */
  'slot.name.placeholder': '名前',
  'slot.name.aria': 'スロット{id}の名前',
  'slot.count': '{n}件',
  'slot.none': '未登録',
  'slot.unregister': '{name} を登録解除',
  'slot.trader': '人物',
  'slot.map': 'マップ',
  'slot.query.placeholder': 'タスク名 / 目標',
  'slot.query.aria': 'タスク名',
  'slot.hint': '条件を入れると候補が出ます',
  'slot.noHit': '該当なし',
  'slot.added': '登録済',
  'slot.add': '＋',
  'slot.more': 'ほか {n} 件（条件を絞ってください）',
  'slot.sort': 'A→Z',
  'slot.sort.title': 'この列の登録済みと候補をタスク名の順に並べます（自分の画面だけ・登録番号は変わりません）',

  /* ---- 準備物 ---- */
  'prep.kind.key': '鍵',
  'prep.kind.plant': '設置するもの',
  'prep.kind.marker': 'マーカー',
  'prep.kind.weapon': '指定武器・装備',
  'prep.kind.build': '組み立て',
  'prep.more': '他{n}件',
  'prep.fold': '畳む',
  'prep.any': 'いずれか',
  'prep.line.title': '持ち込みが要るもの',
  'prep.panel.title': '🎒 準備物',
  'prep.panel.count': '{n}タスク',
  'prep.loading': '準備物データを読み込んでいます…',
  'prep.error': '準備物データを取得できません。',
  'prep.retry': 'もう一度試す',
  'prep.none': '持ち込みが要るタスクはありません',
  'prep.col.items': '持ち込むもの',
  'prep.col.no': 'No',
  'prep.col.no.title': '一覧・マップの数字と同じタスク番号',
  'prep.clear': 'チェックを全部外す',

  /* ---- トップ ---- */
  'home.lead': 'マップにピンと落書きを置いて、最大5人で共有する作戦盤です。',
  'home.lead2': 'ルームを作って、URLを渡すだけで始まります。',
  'home.create': 'ルームを作る',
  'home.join': 'ルームIDで参加',
  'home.joinBtn': '参加',
  'home.badId': 'ルームIDの形式が正しくありません',
  'home.recent': '最近のルーム',
  'home.forget': 'この履歴を消す',
  'home.note': '履歴はこの端末にだけ残ります。',
  'home.warn': 'ルームIDを知っている人は誰でも入れます。秘密の情報を書かないでください。',
  'home.warn2': 'ルームのデータは最終更新から2日で消えます。',
  'home.credit':
    'Escape from Tarkov の非公式ファンプロジェクトです（Battlestate Games とは関係がありません）。非商用・無保証。チート用途には使用できません。',
  'home.ago.now': 'たった今',
  'home.ago.min': '{n}分前',
  'home.ago.hour': '{n}時間前',
  'home.ago.day': '{n}日前',

  /* ---- このアプリについて ---- */
  'about.title': 'このアプリについて',
  'about.back': '← トップへ',
  'about.openPage': 'この内容を固定ページで開く（/about）',

  /* ---- ルームのエラー ---- */
  'roomError.full': 'このルームは満室です（5人まで）',
  'roomError.rate': '送信が多すぎたため切断されました。しばらく待ってから開き直してください',
  'roomError.closed': '本日の新規ルーム作成が上限に達しました。しばらくしてからお試しください',
}

export type MsgKey = keyof typeof ja

/** ja と同じキーを型で強制する。片方だけ増える事故はビルドで落ちる */
const en: Record<MsgKey, string> = {
  /* ---- 言語 ---- */
  'lang.aria': 'Language',

  /* ---- ツール ---- */
  'tool.hand.label': '🖐 Pan',
  'tool.hand.title': 'Drag to move the map. You can also select and move pins',
  'tool.pin.label': '📍 Pin',
  'tool.pin.title': 'Click to drop a pin',
  'tool.pen.label': '✏️ Pen',
  'tool.pen.title': 'Drag to draw on the map (hold Space to pan)',
  'tool.eraser.label': '🧹 Eraser',
  'tool.eraser.title': 'Erase drawings (hold Space to pan)',

  /* ---- ツールバー ---- */
  'app.map': 'Map',
  'app.floor': 'Floor',
  'app.floor.title': 'Choose which floor to overlay on the ground level',
  'app.floor.ground': 'Ground (default)',
  'app.tools': 'Tools',
  'app.penColor': 'Line color',
  'app.pinColor': 'Pin color',
  'app.color': 'Color {c}',
  'app.penWidth': 'Line width',
  'app.penWidth.step': 'Line width: {size}',
  'app.size.thin': 'thin',
  'app.size.medium': 'medium',
  'app.size.thick': 'thick',
  'app.eraserSize': 'Eraser size',
  'app.eraserSize.step': 'Eraser: {size}',
  'app.size.small': 'small',
  'app.size.large': 'large',
  'app.eraserMode': 'Eraser mode',
  'app.eraser.stroke': 'Whole line',
  'app.eraser.stroke.title': 'Erases each line you touch, whole',
  'app.eraser.area': 'Partial',
  'app.eraser.area.title': 'Erases only the part you touch',
  'app.eraser.warn': '⚠ More likely to disconnect. Work in short strokes, or use "Whole line"',
  'app.eraser.warn.title':
    'This is more likely to drop the connection. Work in short strokes, or use the "Whole line" eraser',

  'app.on': 'ON',
  'app.off': 'OFF',
  'app.toggle.doodle': '🖊 Doodles',
  'app.toggle.doodle.title': 'Show or hide drawings',
  'app.toggle.list': '🗒 List',
  'app.toggle.list.title': 'Show or hide the registered task list',
  'app.toggle.locations': '📌 Places',
  'app.toggle.locations.title': 'Overlay the places of registered tasks on the map',
  'app.toggle.taskNames': '🔖 Task names',
  'app.toggle.taskNames.title': 'Show task names above the numbers on the map',
  'app.toggle.labels': '🏷 Labels',
  'app.toggle.labels.title': 'Show street and building names (zoom out to keep only the major ones)',
  'app.toggle.extracts': '🚪 Extracts',
  'app.toggle.extracts.title': 'Show extraction points',

  'app.about': 'ℹ️ About',
  'app.about.title': 'Show the credits and policy of this app',
  'app.name': 'Name',
  'app.state.connecting': 'Connecting…',
  'app.state.open': 'Connected',
  'app.state.closed': 'Disconnected — reconnecting',
  'app.users': '{n} online',

  /* ---- タブ ---- */
  'app.tabs': 'View',
  'app.tab.map': '🗺 Map',
  'app.tab.search': '🔍 Search',

  /* ---- マップ画面 ---- */
  'app.tasksError': 'Could not load task data (map, pins and drawings still work): {msg}',
  'app.pin.label.placeholder': 'Label (e.g. Sniper)',
  'app.pin.note.placeholder': 'Note (optional)',
  'app.close': 'Close',
  'app.delete': 'Delete',
  'app.menu.show': '▾ Show menu',
  'app.menu.hide': '▴ Hide menu',
  'app.menu.show.title': 'Show the share bar, toolbar and tabs',
  'app.menu.hide.title': 'Leave only the map and the task list (fold the share bar, toolbar and tabs)',
  'app.side.open': '⟨ List',
  'app.side.open.title': 'Open the registered task list',
  'app.footbar.counts': '{pins} pins / {strokes} lines',
  'app.footbar.otherFloor': '{n} on another floor',
  'app.footbar.otherFloor.title': 'Switch floors to see them',
  'app.user.viewing': 'Viewing {map}',
  'app.user.noMap': 'No map selected',
  'app.user.drawing': 'Drawing',
  'app.clearPins': 'Clear pins',
  'app.clearPins.confirm': 'Delete every pin on "{map}". Are you sure?',
  'app.clearDoodle': 'Clear drawings',
  'app.clearDoodle.confirm': 'Delete every drawing on "{map}". Are you sure?',

  /* ---- 検索・登録タブ ---- */
  'board.notice.error': 'Could not load task data (json.tarkov.dev): {msg}',
  'board.notice.loading': 'Loading task data…',
  'board.people': 'People',
  'board.people.n': '{n}',
  'board.hint': 'Search across {n} tasks',

  /* ---- 共有バー ---- */
  'share.open.title': 'Open the share bar',
  'share.close.title': 'Fold the share bar',
  'share.label': '🔗 Share this URL',
  'share.copy': 'Copy',
  'share.copied': '✓ Copied',
  'share.prompt': 'Copy this URL',
  'share.reconnect': 'Reconnecting…',
  'share.leave': 'Leave',
  'share.leave.title': 'Leave the room and go back to the top page',
  'share.warn': 'Anyone who knows this URL can join.',
  'share.warn.strong': 'Do not write anything secret here.',
  'share.warn.note': 'A room is deleted 2 days after its last update.',

  /* ---- 一覧パネル ---- */
  'side.title': 'Registered tasks',
  'side.width': 'Task list width',
  'side.close': 'Close the list',

  /* ---- 登録タスク一覧 ---- */
  'tasklist.emptyForMap': 'No tasks for this map ({n} registered in total).',
  'tasklist.emptyForMap.hint': 'Switch maps, or add tasks from the "🔍 Search" tab',
  'tasklist.empty': 'Add tasks from the "🔍 Search" tab and they will be listed here',
  'tasklist.fold.open': 'Expand',
  'tasklist.fold.close': 'Collapse',
  'tasklist.fold.open.title': 'Show objectives and items to bring',
  'tasklist.fold.close.title': 'Show only the task name and item marks',
  'tasklist.no.title': 'No.{n} — the same number as on the map (drag to reorder)',
  'tasklist.wiki': 'Open on the wiki',
  'tasklist.anyMap': 'any map',
  'tasklist.nozone': 'no location',
  'tasklist.nozone.title': 'This task has no coordinate data',
  'tasklist.slot': 'Slot {id}',
  'tasklist.optional': 'optional',
  'tasklist.optional.title': 'You can finish the task without it (its number on the map is grey too)',
  'task.kappa': 'Κ',
  'task.kappa.title': 'Needed for the Kappa container: Collector itself and its 13-task prerequisite chain',
  'tasklist.needs': 'Requires: {names}',
  'tasklist.needs.title': 'You must finish these tasks first',
  'tasklist.sameObjective': '×{n}',
  'tasklist.sameObjective.title': 'Separate objectives that share the same wording upstream, shown as one line',
  'tasklist.remove': 'Remove from everyone',
  'tasklist.show': 'Show on the map',
  'tasklist.hide': 'Hide from the map',
  'tasklist.show.title': 'Put the shapes back on the map (applies to everyone)',
  'tasklist.hide.title': 'Remove the shapes from the map (applies to everyone)',
  'tasklist.up': 'Move up',
  'tasklist.down': 'Move down',
  'tasklist.sort': 'A→Z',
  'tasklist.sort.title': 'Sort this map\'s list by task name (numbers and all, shared with everyone)',

  /* ---- 1人ぶんの列（検索・登録） ---- */
  'slot.name.placeholder': 'Name',
  'slot.name.aria': 'Name for slot {id}',
  'slot.count': '{n}',
  'slot.none': 'Nothing registered',
  'slot.unregister': 'Remove {name}',
  'slot.trader': 'Trader',
  'slot.map': 'Map',
  'slot.query.placeholder': 'Task name / objective',
  'slot.query.aria': 'Task name',
  'slot.hint': 'Enter a condition to see candidates',
  'slot.noHit': 'No matches',
  'slot.added': 'Added',
  'slot.add': '＋',
  'slot.more': '{n} more (narrow your search)',
  'slot.sort': 'A→Z',
  'slot.sort.title': 'Sort this column by task name (your screen only; the registration numbers stay)',

  /* ---- 準備物 ---- */
  'prep.kind.key': 'Keys',
  'prep.kind.plant': 'Items to place',
  'prep.kind.marker': 'Markers',
  'prep.kind.weapon': 'Required weapons / gear',
  'prep.kind.build': 'Builds',
  'prep.more': '{n} more',
  'prep.fold': 'Collapse',
  'prep.any': 'any of',
  'prep.line.title': 'Items you have to bring',
  'prep.panel.title': '🎒 Loadout',
  'prep.panel.count': '{n} tasks',
  'prep.loading': 'Loading item data…',
  'prep.error': 'Could not load item data.',
  'prep.retry': 'Try again',
  'prep.none': 'No task here needs anything brought in',
  'prep.col.items': 'Bring',
  'prep.col.no': 'No',
  'prep.col.no.title': 'The same task number as in the list and on the map',
  'prep.clear': 'Clear all checks',

  /* ---- トップ ---- */
  'home.lead': 'A shared plan board: drop pins and draw on the map with up to 5 people.',
  'home.lead2': 'Create a room, hand out the URL, and you are set.',
  'home.create': 'Create a room',
  'home.join': 'Join with a room ID',
  'home.joinBtn': 'Join',
  'home.badId': 'That is not a valid room ID',
  'home.recent': 'Recent rooms',
  'home.forget': 'Remove from history',
  'home.note': 'The history stays on this device only.',
  'home.warn': 'Anyone who knows the room ID can join. Do not write anything secret there.',
  'home.warn2': 'Room data is deleted 2 days after its last update.',
  'home.credit':
    'An unofficial Escape from Tarkov fan project, not affiliated with Battlestate Games. Non-commercial, no warranty. Not for cheating.',
  'home.ago.now': 'just now',
  'home.ago.min': '{n} min ago',
  'home.ago.hour': '{n} h ago',
  'home.ago.day': '{n} d ago',

  /* ---- このアプリについて ---- */
  'about.title': 'About this app',
  'about.back': '← Back to top',
  'about.openPage': 'Open this as a standalone page (/about)',

  /* ---- ルームのエラー ---- */
  'roomError.full': 'This room is full (5 people max)',
  'roomError.rate': 'Disconnected for sending too much. Wait a moment and reopen the page',
  'roomError.closed': 'The daily limit for new rooms has been reached. Please try again later',
}

const DICTS: Record<Lang, Record<MsgKey, string>> = { ja, en }

export type Params = Record<string, string | number>

/** `{n}` `{name}` だけを差し替える。これ以上の書式は持たない */
export function translate(lang: Lang, key: MsgKey, params?: Params): string {
  const text = DICTS[lang][key] ?? DICTS.en[key] ?? key
  if (!params) return text
  return text.replace(/\{(\w+)\}/g, (whole, name: string) =>
    name in params ? String(params[name]) : whole,
  )
}

/* ------------------------------------------------------------------ *
 * React へ配る
 * ------------------------------------------------------------------ */

export type I18n = {
  lang: Lang
  setLang: (lang: Lang) => void
  t: (key: MsgKey, params?: Params) => string
}

const Ctx = createContext<I18n | null>(null)

export function LangProvider({ children }: { children: ReactNode }) {
  const [lang, setLangState] = useState<Lang>(detectLang)

  // <html lang> も合わせる。読み上げと、ブラウザの翻訳提案のため
  useEffect(() => {
    document.documentElement.lang = lang
  }, [lang])

  const setLang = useCallback((next: Lang) => {
    setLangState(next)
    try {
      localStorage.setItem(LS_LANG, next)
    } catch {
      /* 憶えられなくても切り替えは効かせる */
    }
  }, [])

  const value = useMemo<I18n>(
    () => ({ lang, setLang, t: (key, params) => translate(lang, key, params) }),
    [lang, setLang],
  )

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>
}

/**
 * **`t()` は必ずこれ経由で呼ぶ。** モジュールの外に出して直接呼ぶと、
 * 言語を変えたときにその箇所だけ再描画されない。
 */
export function useI18n(): I18n {
  const ctx = useContext(Ctx)
  if (!ctx) throw new Error('LangProvider の外で useI18n を呼んでいる')
  return ctx
}

/** `JA / EN` の2択。ツールバー・トップ・/about に置く */
export function LangToggle({ className = 'steps' }: { className?: string }) {
  const { lang, setLang, t } = useI18n()
  return (
    <div className={className} role="group" aria-label={t('lang.aria')}>
      {LANGS.map((l) => (
        <button
          key={l.id}
          type="button"
          className={`control seg${lang === l.id ? ' seg--on' : ''}`}
          onClick={() => setLang(l.id)}
          aria-pressed={lang === l.id}
        >
          {l.label}
        </button>
      ))}
    </div>
  )
}
